import { Pencil } from "lucide-react";
import React from "react";
import { useImmer } from "use-immer";

import { IgBasePrimitives } from "@intelligems/ig-design-tokens/constants";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  FlaggedName,
  NameType,
} from "@intelligems/ig-types/fe-only/save-tests";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { Sparkle } from "@src/images/Icons/Sparkle";

export interface AcceptedName {
  id: string;
  value: string;
  nameType: NameType;
}

interface PreSaveNameReviewDialogProps {
  isOpen: boolean;
  flaggedNames: FlaggedName[];
  onConfirm: (accepted: AcceptedName[]) => void;
  onDismiss: () => void;
}

export const PreSaveNameReviewDialog = ({
  isOpen,
  flaggedNames,
  onConfirm,
  onDismiss,
}: PreSaveNameReviewDialogProps) => {
  const suggestedNames = flaggedNames.filter(
    (f) => f.currentValue !== f.originalName && f.nameType === "test_name"
  );
  const theme = useTheme();

  const [editedValues, setEditedValues] = useImmer<Record<string, string>>(() =>
    Object.fromEntries(suggestedNames.map((f) => [f.id, f.currentValue]))
  );

  const handleConfirm = () => {
    onConfirm(
      suggestedNames.map((f) => ({
        id: f.id,
        value: editedValues[f.id] ?? f.currentValue,
        nameType: f.nameType,
      }))
    );
  };

  if (suggestedNames.length === 0) {
    return (
      <IgDialog
        title={""}
        handleClose={onDismiss}
        showDialog={isOpen}
        handleYesClick={onDismiss}
        yesButtonText="OK"
        dialogHeight={null}
        rawContent={
          <Box display="flex" flexDirection="column" gap={2}>
            <IgTypography variant="h1">Test saved successfully!</IgTypography>
          </Box>
        }
      />
    );
  }

  return (
    <IgDialog
      title={""}
      handleClose={onDismiss}
      showDialog={isOpen}
      handleYesClick={handleConfirm}
      yesButtonText="Save new name"
      noButtonText={"Keep current name"}
      dialogHeight={null}
      rawContent={
        <Box display="flex" flexDirection="column" gap={2} mt={-4}>
          <IgTypography variant="h2">
            Test saved successfully. Review new test name
          </IgTypography>

          <IgTypography variant="body2">
            We created a more descriptive name based on your test. Edit it or
            keep yours.
          </IgTypography>
          {suggestedNames.map((flagged) => (
            <Box key={flagged.id} display="flex" flexDirection="column" gap={3}>
              <Box>
                <IgTypography
                  variant="caption"
                  color={theme.palette.text.secondary}
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Current
                </IgTypography>
                <IgTextField
                  multiline
                  maxRows={4}
                  fullWidth
                  value={flagged.originalName}
                  disabled
                />
              </Box>

              <Box>
                <IgTypography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    display: "block",
                    textTransform: "uppercase",
                    mb: 0.5,
                    color: IgBasePrimitives[500],
                    backgroundClip: "text",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Sparkle variant={"purple"} />
                    Suggested
                  </Box>
                </IgTypography>
                <IgTextField
                  multiline
                  maxRows={4}
                  fullWidth
                  value={editedValues[flagged.id] ?? flagged.currentValue}
                  onChange={(e) =>
                    setEditedValues((draft) => {
                      draft[flagged.id] = e.target.value;
                    })
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ alignSelf: "flex-start", mt: 1.2 }}
                        >
                          <Pencil size={14} />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{ "& .MuiInputBase-root": { wordBreak: "break-word" } }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      }
    />
  );
};
